import { isEthereumLikeAsset } from '@exodus/ethereum-lib';
import { InternalError } from '@exodus/web3-errors';
import type {
  AppDeps,
  IMessageSignerModule,
  ISignedTransaction,
  ITransactionSignerModule,
  WalletDeps,
} from '@exodus/web3-types';
import type {
  SignedTransactionData,
  TransactionSigner,
} from '@passkeys/transaction-signer';

import type { Analytics } from '@/background/features/analytics/types';
import type { Asset } from '@/types';
import { referrerURL } from '@/utils/referrer';
import { assertNonEmptyArray } from '@/utils/types';

export const createAppDeps = (exodus): AppDeps => {
  const allAssets: Asset[] = Object.values(exodus.assetsModule.getAssets());
  const evmAssets: Asset[] = allAssets.filter(isEthereumLikeAsset);

  const getAsset = (network: string) => {
    const parts = network.split(':');

    if (parts[0] === 'evm') {
      const chainId = parseInt(parts[1], 16);
      const asset = evmAssets.find((asset) => asset.chainId === chainId);
      if (!asset) {
        throw new Error(`Unsupported asset: '${network}'`);
      }
      return asset;
    }
    const asset = exodus.assetsModule.getAsset(network);
    if (!asset) {
      throw new Error(`Unsupported asset: '${network}'`);
    }
    return asset;
  };

  const getOrigin = () => window.location.origin;

  const scanDomains = () => Promise.resolve(null);

  const { isUnlocked } = createWalletDeps(exodus);

  const getIsConnected = () => isUnlocked!();

  const ensureUntrusted = async () => {
    try {
      const unlocked = await isUnlocked!();
      if (unlocked) {
        await exodus.application.lock();
      }
    } catch (error) {
      exodus.logger.error(error);
      throw new InternalError();
    }
  };

  return {
    // Bypass all approvals as they are handled in the passkeys signer.
    approveConnection: async () => true,
    approveMessage: async () => true,
    approveTransactions: async () => true,
    isAutoApproved: async () => true,
    isTrusted: async () => true,
    ensureTrusted: () => Promise.resolve(),
    // End bypass.
    ensureUntrusted,
    getAsset,
    getOrigin,
    scanDomains,
    getIsConnected,
  };
};

export const createWalletDeps = (exodus): WalletDeps => {
  const isUnlocked = async () => {
    const locked = await exodus.lockedAtom.get();
    return !locked;
  };

  const ensureUnlocked = async () => {
    const unlocked = await isUnlocked();
    if (unlocked) {
      return true;
    }

    try {
      await exodus.application.unlock();
    } catch {
      return false;
    }

    return true;
  };

  return {
    isUnlocked,
    ensureUnlocked,
  };
};

type CreateSignerDepsParams<M> = {
  assetName: string;
  baseAssetName?: string;
  transactionSigner: TransactionSigner;
  messageSigner: IMessageSignerModule<M>;
  analytics: Analytics;
  serializeTransaction?: (
    signedTransaction: SignedTransactionData,
  ) => ISignedTransaction;
};

type SignerDeps<M> = {
  transactionSigner: ITransactionSignerModule<any>;
  messageSigner: IMessageSignerModule<M>;
};

export const createSignerDeps = <M>(
  params: CreateSignerDepsParams<M>,
): SignerDeps<M> => {
  const dappDomain = __STANDALONE_MODE__
    ? undefined
    : new URL(referrerURL).hostname;

  const transactionSigner = {
    signTransaction: async ({ unsignedTx }) => {
      let result: SignedTransactionData | undefined;

      try {
        result = await params.transactionSigner.signTransaction({
          assetName: params.assetName,
          unsignedTx,
        });

        return (params.serializeTransaction?.(result) ??
          result) as ISignedTransaction;
      } finally {
        if (dappDomain) {
          params.analytics.track({
            event: 'DAppSignTransactionRequest',
            properties: {
              dappDomain,
              network: params.baseAssetName ?? params.assetName,
              approved: !!result,
            },
          });
        }
      }
    },
    signTransactions: async (transactions) => {
      let result: SignedTransactionData[] | undefined;

      try {
        const withAssetName = transactions.map((it) => ({
          ...it,
          assetName: params.assetName,
        }));

        assertNonEmptyArray(withAssetName, 'request must contain transactions');
        result = await params.transactionSigner.signTransactions(withAssetName);

        return result.map(
          (it) =>
            (params.serializeTransaction?.(it) ?? it) as ISignedTransaction,
        );
      } finally {
        if (dappDomain) {
          params.analytics.track({
            event: 'DAppSignTransactionRequest',
            properties: {
              dappDomain,
              network: params.baseAssetName ?? params.assetName,
              approved: !!result,
            },
          });
        }
      }
    },
  } satisfies ITransactionSignerModule<any>;

  const messageSigner = {
    signMessage: async ({ walletAccount, message }) => {
      let result: Buffer | undefined;

      try {
        result = await params.messageSigner.signMessage({
          // @ts-expect-error Type falsely expects `baseAssetName` instead of `assetName`.
          assetName: params.assetName,
          walletAccount,
          message,
        });

        return result;
      } finally {
        if (dappDomain) {
          params.analytics.track({
            event: 'DAppSignMessageRequest',
            properties: {
              dappDomain,
              network: params.baseAssetName ?? params.assetName,
              approved: !!result,
            },
          });
        }
      }
    },
  } satisfies IMessageSignerModule<M>;

  return {
    transactionSigner,
    messageSigner,
  };
};
